import React, {useState} from 'react';
import {useIntl} from 'react-intl';
import {FlexColumn, Modal, TextArea, TextField} from '@forecast-it/design-system';
import {MODAL_TYPE, showModal} from '../../../../shared/components/modals/generic_modal_conductor';
import {ModalProps} from '../../../../shared/components/modals/types';
import {DELIVERABLE_DESCRIPTION_MAX_LENGTH, DELIVERABLE_NAME_MAX_LENGTH} from './baselineTemplateDraft';

export type TemplateDeliverableModalPayload = {
	/** The deliverable being edited; absent to add a new one. */
	deliverable?: {name: string; description: string};
	/** Gets the trimmed name and description. Only changes the draft: nothing is sent until the template is saved. */
	onSave: (fields: {name: string; description: string}) => void;
};

export const showTemplateDeliverableModal = (props: TemplateDeliverableModalPayload): void => {
	showModal({type: MODAL_TYPE.TEMPLATE_DELIVERABLE, ...props});
};

const nameErrorId = (name: string): string | undefined => {
	const trimmed = name.trim();
	if (trimmed === '') {
		return 'baseline_templates.errors.deliverable_name_required';
	}
	return trimmed.length > DELIVERABLE_NAME_MAX_LENGTH ? 'baseline_templates.errors.deliverable_name_too_long' : undefined;
};

/** Adds a deliverable to a template, or renames one. The name is required. */
export const TemplateDeliverableModal = ({
	deliverable,
	onSave,
	closeModal,
}: ModalProps<TemplateDeliverableModalPayload>): JSX.Element => {
	const {formatMessage} = useIntl();
	const [name, setName] = useState(deliverable?.name ?? '');
	const [description, setDescription] = useState(deliverable?.description ?? '');
	const [errorId, setErrorId] = useState<string | undefined>();

	const save = () => {
		const problem = nameErrorId(name);
		setErrorId(problem);
		if (problem) {
			return;
		}
		onSave({name: name.trim(), description: description.trim()});
		closeModal();
	};

	const errorText = errorId ? formatMessage({id: errorId}) : undefined;
	return (
		<Modal size={Modal.SIZE.SMALL} onCloseModal={closeModal}>
			<Modal.Title>
				{formatMessage({
					id: deliverable
						? 'deliverables.edit.modal.edit_deliverable'
						: 'deliverables.edit.modal.add_new_deliverable',
				})}
			</Modal.Title>
			<Modal.Content>
				<FlexColumn gap="l">
					<TextField
						label={formatMessage({id: 'baseline_templates.phase.deliverable_name'})}
						data-cy="template-deliverable-name"
						value={name}
						onValueChange={(value: string) => {
							setName(value);
							setErrorId(undefined);
						}}
						hasError={!!errorText}
						errorText={errorText}
					/>
					{/* TextArea hands onChange the value, not an event, and needs an onBlur. */}
					<TextArea
						label={formatMessage({id: 'baseline_templates.phase.deliverable_description'})}
						data-cy="template-deliverable-description"
						isOptional
						height={72}
						maxLength={DELIVERABLE_DESCRIPTION_MAX_LENGTH}
						value={description}
						onChange={(value: string) => setDescription(value)}
						onBlur={() => undefined}
					/>
				</FlexColumn>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton onPress={save} dataCy="template-deliverable-save">
					{formatMessage({
						id: deliverable ? 'deliverables.edit.modal.save_changes' : 'deliverables.edit.modal.add_deliverable',
					})}
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={closeModal} dataCy="template-deliverable-cancel">
					{formatMessage({id: 'common.cancel'})}
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};
